import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useRestClient } from "@/lib/restClient";
import type { DashboardRecord } from "@/lib/dashboard/types";
import { clearDashboardDraftForRecord } from "@/pages/workspace/dashboardDraft";
import { dashboardQueryKey } from "./useDashboard";

interface UpdateDashboardInput {
  id: string;
  catalog_tenant_ids?: number[];
  /** Omitted fields are left unchanged, e.g. to share a dashboard without publishing its draft. */
  config?: DashboardRecord["config"];
  order?: number;
  name?: string;
  slug?: string;
  description?: string | null;
  status?: string;
  state?: "draft" | "saved";
  audience_grants?: NonNullable<DashboardRecord["audience_grants"]>;
  placements?: NonNullable<DashboardRecord["placements"]>;
  /** Administrators only. */
  protected?: boolean;
}

export function useUpdateDashboard() {
  const apiFetch = useRestClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...body }: UpdateDashboardInput) =>
      apiFetch<DashboardRecord>(`/api/dashboards/${id}`, {
        method: "PUT",
        body: JSON.stringify(body),
      }),
    onSuccess: (record, input) => {
      if (input.state === "saved") clearDashboardDraftForRecord(record.id, record.kind);
      // The editor reads the record right away (e.g. "published" vs "never published").
      queryClient.setQueryData(dashboardQueryKey(record.id, record.kind), record);
      void queryClient.invalidateQueries({ queryKey: ["dashboards"] });
    },
  });
}
